<script setup lang="ts">
import type { PermissionRoleInDb, PermissionRoleCreate } from '@/api';
import {
  type DataTableColumns,
  type DataTableSortState,
  NIcon,
  NButton,
  NPopconfirm,
  NSpace,
  NTag,
} from 'naive-ui';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import PeoplePageHeader from '@/components/organisation/PeoplePageHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import { IconDelete, IconEdit, IconShieldCheck } from '@/components/design/icons';
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { RouterLink, useRouter, type RouteLocationRaw } from 'vue-router';
import { PermissionsService } from '@/services/permissionsService';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import { useAuthStore } from '@/stores/auth';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import RoleFormDialog from '@/components/permissions/RoleFormDialog.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { permissionRoleRoute } from '@/router/links';

// ==================== Local State ====================
const roles = ref<PermissionRoleInDb[]>([]);
const loading = ref<boolean>(false);
const totalCount = ref<number>(0);

const router = useRouter();
const message = useGaiaToast();
const authStore = useAuthStore();
const saving = ref<boolean>(false);
const { currentPage, pageSize, searchQuery, sortKey, sortDirection, sortActivated } =
  useListQuerySync();
const showModal = ref<boolean>(false);
/** The role in the dialog; null while creating one. */
const editingRole = ref<PermissionRoleInDb | null>(null);
const saveError = ref<string | null>(null);

// ==================== Computed Pagination Config ====================
const paginationConfig = usePaginationConfig(currentPage, pageSize, totalCount);

// ==================== Data Fetching ====================
async function fetchRoles(): Promise<void> {
  loading.value = true;

  try {
    const result = await PermissionsService.searchRoles({
      text: searchQuery.value || '',
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
    });

    roles.value = result.results;
    totalCount.value = result.result_count;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch roles';
    message.error(errorMessage);
  } finally {
    loading.value = false;
  }
}

// ==================== Sort Handling ====================

function handleSortChange(sorter: DataTableSortState | DataTableSortState[] | null): void {
  if (!sorter || Array.isArray(sorter)) return;

  if (sorter.order === false) {
    sortKey.value = 'created_at';
    sortDirection.value = 'desc';
    sortActivated.value = false;
    currentPage.value = 1;
    void fetchRoles();
    return;
  }

  sortActivated.value = true;
  sortKey.value = String(sorter.columnKey);
  sortDirection.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  currentPage.value = 1;
  void fetchRoles();
}

// A new search starts from the first page and drops any manual sort. The field
// debounces its own keystrokes and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1;
  sortActivated.value = false;
  await fetchRoles();
}

// ==================== Table Columns ====================
const columns = computed((): DataTableColumns<PermissionRoleInDb> => [
  {
    title: 'Name',
    key: 'name',
    width: 200,
    sortOrder:
      sortActivated.value && sortKey.value === 'name'
        ? sortDirection.value === 'asc'
          ? 'ascend'
          : 'descend'
        : false,
    sorter: true,
    render(row): VNode {
      return h(
        RouterLink,
        {
          to: roleRoute(row),
          style: 'color: inherit; text-decoration: none',
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
          },
        },
        { default: () => row.name }
      );
    },
  },
  {
    title: 'Description',
    key: 'description',
    width: 320,
    ellipsis: { tooltip: true },
  },
  {
    title: 'Protected',
    key: 'protected',
    width: 100,
    render(row): VNode {
      return row.protected
        ? h(NTag, { type: 'warning', size: 'small' }, { default: () => 'Protected' })
        : h('span', '—');
    },
  },
  {
    title: 'Created At',
    key: 'created_at',
    width: 180,
    sortOrder:
      sortActivated.value && sortKey.value === 'created_at'
        ? sortDirection.value === 'asc'
          ? 'ascend'
          : 'descend'
        : false,
    sorter: true,
    render(row): VNode {
      return h('span', formatDateTimeForTable(row.created_at));
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 140,
    fixed: 'right',
    render(row): VNode {
      const buttons: VNode[] = [];
      const isAdmin = authStore.userPermissions.is_org_admin;
      if (isAdmin) {
        buttons.push(
          h(
            NButton,
            {
              quaternary: true,
              size: 'small',
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
                handleEdit(row);
              },
            },
            { icon: () => h(NIcon, null, { default: () => h(IconEdit) }) }
          )
        );
      }
      // Protected roles can never be deleted, the editable Organisation Member included.
      if (isAdmin && !row.protected) {
        buttons.push(
          h(
            NPopconfirm,
            {
              onPositiveClick: () => handleDelete(row.uid),
            },
            {
              default: () => 'Are you sure you want to delete this role?',
              trigger: () =>
                h(
                  NButton,
                  {
                    quaternary: true,
                    size: 'small',
                    type: 'error',
                    onClick: (e: MouseEvent) => {
                      e.stopPropagation();
                    },
                  },
                  { icon: () => h(NIcon, null, { default: () => h(IconDelete) }) }
                ),
            }
          )
        );
      }
      return h(
        NSpace,
        { align: 'center', justify: 'center', size: [8, 0], inline: true, wrap: false },
        { default: () => buttons }
      );
    },
  },
]);

// Refetch when page or size changes (usePaginationConfig only updates the refs).
watch([currentPage, pageSize], () => {
  void fetchRoles();
});

// ==================== Modal Handlers ====================
function openRoleDialog(role: PermissionRoleInDb | null): void {
  if (!authStore.userPermissions.is_org_admin) return;
  editingRole.value = role;
  saveError.value = null;
  showModal.value = true;
}

function handleCreateClick(): void {
  openRoleDialog(null);
}

function handleEdit(role: PermissionRoleInDb): void {
  openRoleDialog(role);
}

async function handleSave(payload: PermissionRoleCreate): Promise<void> {
  saving.value = true;
  saveError.value = null;
  const editing = editingRole.value;
  try {
    if (editing) {
      await PermissionsService.updateRole(editing.uid, payload);
      message.success('Role updated');
    } else {
      await PermissionsService.createRole(payload);
      message.success('Role created');
    }
    showModal.value = false;
    await fetchRoles();
  } catch (error) {
    saveError.value =
      error instanceof Error ? error.message : `Failed to ${editing ? 'update' : 'create'} role`;
  } finally {
    saving.value = false;
  }
}

async function handleDelete(uid: string): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) return;
  try {
    await PermissionsService.deleteRole(uid);
    message.success('Role deleted successfully');
    await fetchRoles();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to delete role';
    message.error(errorMessage);
  }
}

function roleRoute(row: PermissionRoleInDb): RouteLocationRaw {
  return permissionRoleRoute(row.uid);
}

function handleRowClick(row: PermissionRoleInDb): void {
  void router.push(roleRoute(row));
}

// ==================== Lifecycle ====================
onMounted(async (): Promise<void> => {
  await fetchRoles();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.people }}</n-breadcrumb-item>
    </template>

    <people-page-header active="roles">
      <template #actions>
        <gaia-search-input
          v-model:value="searchQuery"
          placeholder="Search roles"
          :debounce="500"
          style="width: 220px"
          @search="applySearch"
        />
        <gaia-button
          v-if="authStore.userPermissions.is_org_admin"
          type="primary"
          @click="handleCreateClick"
        >
          <template #icon>
            <n-icon :component="IconShieldCheck" />
          </template>
          Create role
        </gaia-button>
      </template>
    </people-page-header>

    <gaia-table
      :columns="columns"
      :data="roles"
      :loading="loading"
      :remote="true"
      :pagination="paginationConfig"
      :row-props="
        (row: PermissionRoleInDb) => ({
          style: { cursor: 'pointer' },
          onClick: () => handleRowClick(row),
        })
      "
      @update:sorter="handleSortChange"
    />

    <role-form-dialog
      v-model:show="showModal"
      :role="editingRole"
      :loading="saving"
      :error="saveError ?? undefined"
      @submit="handleSave"
    />
  </Layout>
</template>

<style scoped>
:deep(.n-data-table .n-data-table-th) {
  word-break: normal !important;
  white-space: normal !important;
  overflow-wrap: normal !important;
  word-wrap: normal !important;
  hyphens: none !important;
}

:deep(.n-data-table .n-data-table-th .n-data-table-th__title) {
  word-break: normal !important;
  white-space: normal !important;
  overflow-wrap: normal !important;
  word-wrap: normal !important;
  line-height: 1.4 !important;
}
</style>
